import { useEffect, useMemo, useState } from "react";
import { http, toastApiError } from "../../lib/api";
import { fmtTL } from "../../lib/format";
import { trUpper } from "../../lib/uppercase";
import { ArrowLeftRight, TrendingDown, TrendingUp, Minus, FileSpreadsheet } from "lucide-react";

const sel = "px-3 py-2 text-sm border border-slate-300 rounded-md bg-white font-semibold";
const keyOf = (it) => it.budget_item_id || `n:${trUpper(it.name || "")}`;

export function useCostCompare(costs, { projectId, periodA, periodB, categoryId }) {
  return useMemo(() => {
    const pick = (per) => costs.filter((k) => k.project_id === projectId && k.period === per && (!categoryId || k.category_id === categoryId));
    const agg = (per) => {
      const m = new Map();
      pick(per).forEach((k) => (k.items || []).forEach((it) => {
        const key = keyOf(it); const cur = m.get(key) || { name: it.name, a: 0 };
        m.set(key, { ...cur, a: cur.a + (Number(it.amount) || 0) });
      }));
      return m;
    };
    const A = agg(periodA), B = agg(periodB);
    const keys = [...new Set([...A.keys(), ...B.keys()])];
    const rows = keys.map((key) => {
      const a = A.get(key)?.a || 0, b = B.get(key)?.a || 0;
      return { key, name: (B.get(key) || A.get(key)).name, a, b, diff: b - a, pct: a ? ((b - a) / a) * 100 : null, status: !A.has(key) ? "new" : !B.has(key) ? "gone" : "" };
    });
    const tA = rows.reduce((s, r) => s + r.a, 0), tB = rows.reduce((s, r) => s + r.b, 0);
    return { rows, totalA: tA, totalB: tB, diff: tB - tA, pct: tA ? ((tB - tA) / tA) * 100 : null };
  }, [costs, projectId, periodA, periodB, categoryId]);
}

const DiffCell = ({ diff, pct, muted }) => {
  if (muted || Math.abs(diff) < 0.005) return <span className="inline-flex items-center gap-1 text-slate-400"><Minus className="w-3 h-3" /> {fmtTL(0)}</span>;
  const up = diff > 0;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${up ? "text-red-700" : "text-emerald-700"}`}>
      {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
      {up ? "+" : "−"}{fmtTL(Math.abs(diff))}{pct != null && <span className="text-[11px] font-normal">({up ? "+" : "−"}%{Math.abs(pct).toLocaleString("tr-TR", { maximumFractionDigits: 1 })})</span>}
    </span>
  );
};

export function CostCompare({ costs: costsProp, testid = "pcost-compare", initialProjectId = "" }) {
  const [own, setOwn] = useState([]);
  useEffect(() => { if (!costsProp) http.get("/project-costs").then((r) => setOwn(r.data || [])).catch(() => {}); }, [costsProp]);
  const costs = costsProp || own;
  const projects = useMemo(() => [...new Map(costs.map((k) => [k.project_id, k.project_name || "—"])).entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, "tr")), [costs]);
  const [projectId, setProjectId] = useState(initialProjectId);
  useEffect(() => { if (!projectId && projects.length) setProjectId(projects.find((p) => p.value === initialProjectId)?.value || projects[0].value); }, [projects, projectId, initialProjectId]);
  const periods = useMemo(() => [...new Set(costs.filter((k) => k.project_id === projectId).map((k) => k.period))].sort(), [costs, projectId]);
  const categories = useMemo(() => [...new Map(costs.filter((k) => k.project_id === projectId).map((k) => [k.category_id, k.category_name || "—"])).entries()], [costs, projectId]);
  const [periodA, setPeriodA] = useState(""); const [periodB, setPeriodB] = useState(""); const [categoryId, setCategoryId] = useState("");
  useEffect(() => { setPeriodA(periods[periods.length - 2] || ""); setPeriodB(periods[periods.length - 1] || ""); setCategoryId(""); }, [periods]);
  const cmp = useCostCompare(costs, { projectId, periodA, periodB, categoryId });
  const exportXlsx = async () => {
    try {
      const r = await http.get("/project-costs/compare-export", { params: { project_id: projectId, period_a: periodA, period_b: periodB, category_id: categoryId }, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `maliyet_karsilastirma_${periodA}_${periodB}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toastApiError(e); }
  };

  if (!projects.length) return null;
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-bold text-slate-900 uppercase tracking-wide inline-flex items-center gap-1.5"><ArrowLeftRight className="w-4 h-4 text-amber-600" /> Dönem Karşılaştırma</span>
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid={`${testid}-project`} className={sel}>{projects.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}</select>
        <select value={periodA} onChange={(e) => setPeriodA(e.target.value)} data-testid={`${testid}-a`} className={sel}><option value="">Dönem A</option>{periods.map((p) => <option key={p} value={p}>{p}</option>)}</select>
        <span className="text-slate-400 text-xs">→</span>
        <select value={periodB} onChange={(e) => setPeriodB(e.target.value)} data-testid={`${testid}-b`} className={sel}><option value="">Dönem B</option>{periods.map((p) => <option key={p} value={p}>{p}</option>)}</select>
        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} data-testid={`${testid}-cat`} className={`${sel} font-normal`}><option value="">Tüm ana başlıklar</option>{categories.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        {periodA && periodB && cmp.rows.length > 0 && (
          <button type="button" onClick={exportXlsx} data-testid={`${testid}-export`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-50 text-emerald-800 text-xs font-semibold border border-emerald-200 hover:bg-emerald-100">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel İndir
          </button>
        )}
      </div>
      {periods.length < 2 && <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid={`${testid}-hint`}>Bu proje için karşılaştırılacak en az iki dönem maliyeti gerekir (şu an {periods.length} dönem).</div>}
      {periodA && periodB && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full tbl">
            <thead className="bg-slate-50">
              <tr>
                {["Gider Kalemi", periodA, periodB, "Fark"].map((h, i) => <th key={h} className={`px-3 py-2 text-xs uppercase tracking-wider text-slate-600 font-semibold ${i ? "text-right" : "text-left"}`}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {cmp.rows.map((r) => (
                <tr key={r.key} className="border-t border-slate-100" data-testid={`${testid}-row-${r.key}`}>
                  <td className="px-3 py-1.5 text-xs font-medium">
                    {r.name}
                    {r.status === "new" && <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-bold uppercase">Yeni</span>}
                    {r.status === "gone" && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] font-bold uppercase">Kalktı</span>}
                  </td>
                  <td className="numeric-cell px-3 py-1.5 text-xs w-40">{r.status === "new" ? "—" : fmtTL(r.a)}</td>
                  <td className="numeric-cell px-3 py-1.5 text-xs w-40">{r.status === "gone" ? "—" : fmtTL(r.b)}</td>
                  <td className="numeric-cell px-3 py-1.5 text-xs w-56"><DiffCell diff={r.diff} pct={r.pct} /></td>
                </tr>
              ))}
              {cmp.rows.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-xs text-slate-500">Seçilen dönemlerde kalem yok</td></tr>}
            </tbody>
            <tfoot className="bg-slate-100 border-t-2 border-slate-300">
              <tr>
                <td className="px-3 py-2 text-xs font-bold uppercase tracking-wider">Toplam</td>
                <td className="numeric-cell px-3 py-2 text-sm font-bold" data-testid={`${testid}-total-a`}>{fmtTL(cmp.totalA)}</td>
                <td className="numeric-cell px-3 py-2 text-sm font-bold" data-testid={`${testid}-total-b`}>{fmtTL(cmp.totalB)}</td>
                <td className="numeric-cell px-3 py-2 text-sm" data-testid={`${testid}-total-diff`}><DiffCell diff={cmp.diff} pct={cmp.pct} /></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
